import { action, makeObservable, observable, runInAction } from 'mobx';
import { get } from 'stores/_services/dataService';
import rootStore from 'stores/index';

const DEFAULT_LICENSE_FEATURES = {
    dashboard: false,
    extensions: false,
    enableCloudScanning: false,
    enableIntegrations: false,
    search: false,
    executiveThreatReport: false,
    enableRollback: false
};

export interface ILicenseFeatures {
    dashboard: boolean;
    extensions: boolean;
    enableCloudScanning: boolean;
    enableIntegrations: boolean;
    search: boolean;
    executiveThreatReport: boolean;
    enableRollback: boolean;
}

export interface ILicense {
    avExpiration: string;
    avHostLimit: number;
    avHostLimitUsed: number;
    customer: string;
    dattoAvExpired: boolean;
    dattoEdrExpired: boolean;
    displayInstanceDeleteWarning: boolean;
    edrExpiration: string;
    edrHostLimit: number;
    edrHostLimitUsed: number;
    features: {
        dashboard: boolean;
        extensions: boolean;
        enableCloudScanning: boolean;
        enableIntegrations: boolean;
        search: boolean;
        executiveThreatReport: boolean;
        enableRollback: boolean;
        newFeaturesRollback: boolean;
    };
    hasAvLicense: boolean;
    hasEdrLicense: boolean;
    instanceObliterDate: string;
    isOffline: boolean;
    isRMM: boolean;
    metrics: {
        endpointLast30: number;
    };
    rmmAccountUid: string | null;
    rmmPlatformName: string | null;
    sf: boolean;
    type: string;
    variant: string;
}

export default class LicenseStore {
    features = DEFAULT_LICENSE_FEATURES;
    license = {};
    isLoaded = false;
    isOffline = false;
    dattoEdrExpired = false;
    loginPortal = null;
    rmmAccountUid = null;
    rmmPlatformName = null;
    isTest = false;

    constructor() {
        makeObservable(this, {
            features: observable,
            license: observable,
            isLoaded: observable,
            isOffline: observable,
            dattoEdrExpired: observable,
            loginPortal: observable,
            rmmAccountUid: observable,
            rmmPlatformName: observable,
            isTest: observable,
            getLicense: action
        });
    }

    getEdrInactiveMessage() {
        return 'Your Datto EDR license is not active. You must activate your license to enable Datto EDR features.';
    }

    getAvInactiveMessage() {
        return 'Your Datto AV license is not active. You must activate your license to enable Datto AV features.';
    }

    async getLicense(force: boolean) {
        if (!force && this.isLoaded) {
            return this.features;
        }

        const { data } = await get('license');
        const {
            features,
            loginPortal,
            isTest,
            dattoEdrExpired,
            hasEdrLicense,
            hasAvLicense,
            displayInstanceDeleteWarning,
            instanceObliterDate,
            isOffline,
            accountUid,
            platformName,
            ...license
        } = data;

        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.features = features;
            this.dattoEdrExpired = dattoEdrExpired;
            this.isOffline = isOffline;
            this.loginPortal = loginPortal;
            this.rmmAccountUid = accountUid;
            this.rmmPlatformName = platformName;
            this.isTest = isTest;
            this.license = {
                ...license,
                displayInstanceDeleteWarning,
                instanceObliterDate,
                features,
                loginPortal,
                dattoEdrExpired,
                rmmAccountUid: this.rmmAccountUid,
                rmmPlatformName: this.rmmPlatformName,
                isOffline,
                hasEdrLicense,
                hasAvLicense
            };
            this.isLoaded = true;
        });
        //Override the internal personnel condition and allow the current rules behavior if we want to enable it for specific tenants.
        features.rule && rootStore.authStore.setAccessRule(features.rule);

        return features;
    }
}
